import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Button, CircularProgress } from '@material-ui/core'
import { V4_T } from '../../PropertyDetail/propertyDetailHelpers'

const SERIF = "'Iowan Old Style', 'Palatino Linotype', Georgia, serif"
const labelStyle = {
  fontFamily: 'monospace',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: V4_T.muteSoft,
  textTransform: 'uppercase'
}

const formatNumber = value => Math.round(Number(value || 0)).toLocaleString()
const formatDate = value => {
  const date = value ? new Date(value) : null
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—'
}

const metricStyle = (active = false) => ({
  border: `1px solid ${active ? V4_T.rust : V4_T.line}`,
  background: active ? V4_T.rustSoft : '#fff',
  borderRadius: 9,
  padding: '8px 12px',
  minWidth: 112,
  textAlign: 'left'
})

const boundaryLabel = source => ({
  manual: 'Manual',
  county: 'County',
  missing: 'Missing'
}[source] || 'Missing')

export default function PropertyGroupDashboard ({ url }) {
  const [data, setData] = useState({ stats: {}, groups: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [attentionOnly, setAttentionOnly] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get(`${url}/api/customer/operational-property-groups/dashboard`)
      setData({
        stats: response.data?.stats || {},
        groups: Array.isArray(response.data?.groups) ? response.data.groups : []
      })
    } catch (requestError) {
      console.error('Failed to fetch property-group dashboard:', requestError)
      setError(requestError?.response?.data?.message || requestError.message || 'Unable to load property groups')
      setData({ stats: {}, groups: [] })
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => { load() }, [load])

  const groups = useMemo(
    () => (attentionOnly ? data.groups.filter(group => group.health === 'attention') : data.groups),
    [attentionOnly, data.groups]
  )
  const stats = data.stats || {}

  return (
    <section
      aria-label="Property groups dashboard"
      style={{ margin: '16px 0 22px', border: `1px solid ${V4_T.line}`, borderRadius: 12, background: V4_T.surface2, overflow: 'hidden' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderBottom: `1px solid ${V4_T.line}` }}>
        <div>
          <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 20, color: V4_T.ink }}>Property groups</div>
          <div style={{ fontSize: 11.5, color: V4_T.muteSoft }}>
            Shared-area and property-boundary health across every operational group
          </div>
        </div>
        <Button size="small" variant="outlined" onClick={load} disabled={loading} style={{ marginLeft: 'auto' }}>
          Refresh
        </Button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 16px' }}>
        <div style={metricStyle()}>
          <div style={labelStyle}>Groups</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700 }}>{stats.totalGroups || 0}</div>
        </div>
        <div style={metricStyle()}>
          <div style={labelStyle}>Grouped properties</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700 }}>{stats.groupedProperties || 0}</div>
        </div>
        <button
          type="button"
          aria-label="Groups needing attention"
          aria-pressed={attentionOnly}
          onClick={() => setAttentionOnly(value => !value)}
          style={{ ...metricStyle(Boolean(stats.groupsNeedingAttention)), cursor: 'pointer' }}
        >
          <div style={labelStyle}>Need attention</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: stats.groupsNeedingAttention ? V4_T.rust : V4_T.green }}>
            {stats.groupsNeedingAttention || 0}
          </div>
        </button>
        <div style={metricStyle()}>
          <div style={labelStyle}>Shared areas</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700 }}>{stats.totalSharedAreas || 0}</div>
        </div>
        <div style={metricStyle(Boolean(stats.missingBoundaryProperties))}>
          <div style={labelStyle}>Missing boundaries</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: stats.missingBoundaryProperties ? V4_T.rust : V4_T.green }}>
            {stats.missingBoundaryProperties || 0}
          </div>
        </div>
        <div style={metricStyle(Boolean(stats.overlappingBoundaryGroups))}>
          <div style={labelStyle}>Boundary conflicts</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: stats.overlappingBoundaryGroups ? V4_T.rust : V4_T.green }}>
            {stats.overlappingBoundaryGroups || 0}
          </div>
        </div>
        <div style={metricStyle(Boolean(stats.unallocatedGroups))}>
          <div style={labelStyle}>Unallocated groups</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: stats.unallocatedGroups ? V4_T.rust : V4_T.green }}>
            {stats.unallocatedGroups || 0}
          </div>
        </div>
        <div style={metricStyle(Boolean((stats.unslicedAreas || 0) + (stats.staleSliceAreas || 0)))}>
          <div style={labelStyle}>Slice gaps</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: (stats.unslicedAreas || stats.staleSliceAreas) ? V4_T.rust : V4_T.green }}>
            {(stats.unslicedAreas || 0) + (stats.staleSliceAreas || 0)}
          </div>
        </div>
        <div style={metricStyle(Boolean(stats.projectionGaps))}>
          <div style={labelStyle}>Takeoff sync gaps</div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: stats.projectionGaps ? V4_T.rust : V4_T.green }}>
            {stats.projectionGaps || 0}
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 24, color: V4_T.mute }}>
          <CircularProgress size={18} /> Loading property groups…
        </div>
      )}
      {!loading && error && (
        <div style={{ margin: '0 16px 14px', padding: 10, color: V4_T.rust, background: V4_T.rustSoft, borderRadius: 7, fontSize: 12 }}>
          {error}
        </div>
      )}
      {!loading && !error && groups.length === 0 && (
        <div style={{ padding: '18px 16px 22px', color: V4_T.muteSoft, fontSize: 12 }}>
          {attentionOnly ? 'No property groups need attention.' : 'No operational property groups have been created.'}
        </div>
      )}

      {!loading && groups.map(group => {
        const warningIssues = (group.warnings || []).filter(warning => warning.severity === 'warning')
        const infoIssues = (group.warnings || []).filter(warning => warning.severity !== 'warning')
        const firstMember = group.members?.[0]
        return (
          <div
            key={group._id}
            data-testid="property-group-row"
            style={{ margin: '0 16px 12px', background: '#fff', border: `1px solid ${group.health === 'attention' ? `${V4_T.rust}77` : V4_T.line}`, borderRadius: 9, padding: 12 }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 180, flex: '0 0 220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <strong style={{ color: V4_T.ink, fontSize: 14 }}>{group.name || 'Property group'}</strong>
                  <span style={{ fontSize: 9.5, fontFamily: 'monospace', fontWeight: 700, color: group.health === 'attention' ? V4_T.rust : '#fff', background: group.health === 'attention' ? V4_T.rustSoft : V4_T.green, borderRadius: 999, padding: '2px 7px' }}>
                    {group.health === 'attention' ? 'NEEDS ATTENTION' : 'READY'}
                  </span>
                </div>
                <div style={{ color: V4_T.muteSoft, fontSize: 11, marginTop: 3 }}>
                  Updated {formatDate(group.updatedAt)} · revision {group.revision || 1}
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={labelStyle}>Members</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                  {(group.members || []).map(member => (
                    <a
                      key={member.jobSiteId}
                      href={`/site-takeoff-v3/${member.jobSiteId}`}
                      title={[member.clientName, member.address].filter(Boolean).join(' · ')}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none', color: V4_T.ink2, border: `1px solid ${member.boundarySource === 'missing' ? V4_T.rust : V4_T.line}`, borderRadius: 999, padding: '3px 8px', fontSize: 11 }}
                    >
                      {member.propertyName}
                      <span style={{ color: member.boundarySource === 'missing' ? V4_T.rust : V4_T.muteSoft, fontSize: 9.5 }}>
                        {boundaryLabel(member.boundarySource)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(74px, auto))', gap: 8, textAlign: 'right' }}>
                <div><div style={labelStyle}>Areas</div><strong>{group.areaCount || 0}</strong></div>
                <div>
                  <div style={labelStyle}>Slices</div>
                  <strong>{group.currentSliceCount || 0}</strong>
                  <div style={{ color: V4_T.muteSoft, fontSize: 9.5 }}>{group.projectedSliceCount || 0} synced</div>
                </div>
                <div>
                  <div style={labelStyle}>Unallocated</div>
                  <strong style={{ color: group.unallocatedSquareFeet ? V4_T.rust : V4_T.ink }}>
                    {formatNumber(group.unallocatedSquareFeet)} SF
                  </strong>
                </div>
              </div>

              {firstMember && (
                <Button
                  component="a"
                  href={`/site-takeoff-v3/${firstMember.jobSiteId}`}
                  size="small"
                  variant="outlined"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Open group
                </Button>
              )}
            </div>

            {(warningIssues.length > 0 || infoIssues.length > 0) && (
              <div style={{ marginTop: 9, paddingTop: 8, borderTop: `1px solid ${V4_T.line}` }}>
                {warningIssues.map((warning, index) => (
                  <div key={`${warning.code}-${index}`} style={{ color: V4_T.rust, fontSize: 11.5, marginTop: index ? 3 : 0 }}>
                    ! {warning.message}
                  </div>
                ))}
                {infoIssues.map((warning, index) => (
                  <div key={`${warning.code}-${index}`} style={{ color: V4_T.muteSoft, fontSize: 11.5, marginTop: 3 }}>
                    {warning.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </section>
  )
}
